import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Edit, Trash2, Shield, ShieldCheck, FileText, Image, Video, HardDrive, Clock, Ban, CheckCircle, AlertTriangle, History, Bell, Store, ExternalLink, Images } from 'lucide-react';
import { format } from 'date-fns';
import { AdminUser } from '../../services/userManagementService';
import { useUserStats } from '../../hooks/useUserStats';
import { formatFileSize } from '../../utils/formatFileSize';
import AccountStatusBadge from './AccountStatusBadge';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

interface UserManagementTableProps {
  users: AdminUser[];
  onEditUser: (user: AdminUser) => void;
  onDeleteUser: (user: AdminUser) => void;
  onSuspendUser: (user: AdminUser) => void;
  onBanUser: (user: AdminUser) => void;
  onReactivateUser: (user: AdminUser) => void;
  onAddInfraction: (user: AdminUser) => void;
  onViewHistory: (user: AdminUser) => void;
  onSendNotification: (user: AdminUser) => void;
  onViewPosts: (user: AdminUser) => void;
  isUpdating: boolean;
  isDeleting: boolean;
  isSuspending: boolean;
  isBanning: boolean;
  isReactivating: boolean;
}

const UserManagementTable: React.FC<UserManagementTableProps> = ({
  users,
  onEditUser,
  onSendNotification,
  onDeleteUser,
  onSuspendUser,
  onBanUser,
  onReactivateUser,
  onAddInfraction,
  onViewHistory,
  onViewPosts,
  isUpdating,
  isDeleting,
  isSuspending,
  isBanning,
  isReactivating
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userIds = users.map(user => user.id);
  const { data: userStats = [] } = useUserStats(userIds);

  const { data: providerData } = useQuery({
    queryKey: ['user-providers', userIds],
    queryFn: async () => {
      if (userIds.length === 0) return new Map();
      
      const { data, error } = await supabase
        .from('service_providers')
        .select('id, user_id, business_name, verified, subscription_status, provider_payment_status')
        .in('user_id', userIds)
        .is('deleted_at', null);

      if (error) throw error;
      
      const providerMap = new Map();
      data?.forEach(provider => {
        providerMap.set(provider.user_id, provider);
      });
      return providerMap;
    },
    enabled: userIds.length > 0
  });

  const getUserStats = (userId: string) => {
    return userStats.find(stat => stat.userId === userId) || {
      postCount: 0,
      photoCount: 0,
      videoCount: 0,
      totalFileSize: 0
    };
  };

  const getSubscriptionBadge = (tier: string) => {
    const tierKey = tier as 'free' | 'standard' | 'premium' | 'ultimate';
    const label = t(`adminPanel.subscriptionTiers.${tierKey}`, tier);
    switch (tier) {
      case 'free':
        return <Badge variant="secondary">{label}</Badge>;
      case 'premium':
        return <Badge variant="default">{label}</Badge>;
      case 'ultimate':
        return <Badge variant="destructive">{label}</Badge>;
      default:
        return <Badge variant="outline">{label}</Badge>;
    }
  };

  const getAdminRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return <Badge variant="destructive" className="flex items-center gap-1">
          <ShieldCheck className="h-3 w-3" />
          {t('adminPanel.adminRoles.admin')}
        </Badge>;
      case 'moderator':
        return <Badge variant="secondary" className="flex items-center gap-1">
          <Shield className="h-3 w-3" />
          {t('adminPanel.adminRoles.moderator')}
        </Badge>;
      default:
        return <Badge variant="outline">{t('adminPanel.adminRoles.user')}</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      {users.map((user) => {
        const stats = getUserStats(user.id);
        return (
          <Card key={user.id} className="border-l-4 border-l-primary">
            <CardContent className="p-3 sm:pt-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                <div className="space-y-2 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h4 className="font-semibold text-lg">
                      {user.full_name || t('adminPanel.noName')}
                    </h4>
                    <AccountStatusBadge 
                      status={user.account_status} 
                      suspendedUntil={user.suspended_until}
                    />
                    {getSubscriptionBadge(user.subscription_tier)}
                    {getAdminRoleBadge(user.admin_role)}
                    <Badge 
                      variant={
                        (user.strike_count ?? 0) === 0 ? "secondary" :
                        (user.strike_count ?? 0) === 1 ? "default" :
                        (user.strike_count ?? 0) === 2 ? "outline" :
                        "destructive"
                      }
                      className="flex items-center gap-1"
                    >
                      {(user.strike_count ?? 0) > 0 ? (
                        <AlertTriangle className="h-3 w-3" />
                      ) : (
                        <Shield className="h-3 w-3" />
                      )}
                      {user.strike_count ?? 0} {(user.strike_count ?? 0) !== 1 ? t('adminPanel.strikes') : t('adminPanel.strike')}
                    </Badge>
                  </div>
                  
                  {user.suspension_reason && (
                    <div className="text-sm bg-orange-50 border border-orange-200 rounded p-2">
                      <span className="font-semibold text-orange-900">{t('adminPanel.reason')}: </span>
                      <span className="text-orange-800">{user.suspension_reason}</span>
                    </div>
                  )}
                  
                  <p className="text-sm text-muted-foreground">
                    📧 {user.email}
                  </p>
                  
                  <div className="flex gap-4 text-sm flex-wrap">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <FileText className="h-4 w-4" />
                      <span>{stats.postCount} {t('adminPanel.posts')}</span>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Image className="h-4 w-4" />
                      <span>{stats.photoCount} {t('adminPanel.photos')}</span>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Video className="h-4 w-4" />
                      <span>{stats.videoCount} {t('adminPanel.videos')}</span>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <HardDrive className="h-4 w-4" />
                      <span>{formatFileSize(stats.totalFileSize)} {t('adminPanel.stored')}</span>
                    </div>
                  </div>
                  
                  {user.bio && (
                    <p className="text-sm text-muted-foreground">
                      📝 {user.bio}
                    </p>
                  )}
                  
                  {user.location && (
                    <p className="text-sm text-muted-foreground">
                      📍 {user.location}
                    </p>
                  )}
                  
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <span>
                      {t('adminPanel.createdAt')}: {format(new Date(user.created_at), 'dd/MM/yyyy HH:mm')}
                    </span>
                    <span>
                      {t('adminPanel.updatedAt')}: {format(new Date(user.updated_at), 'dd/MM/yyyy HH:mm')}
                    </span>
                  </div>

                  {providerData?.get(user.id) && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded flex flex-wrap items-center gap-2">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <Store className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        <span className="text-sm font-semibold text-blue-900 break-words">
                          {t('adminPanel.serviceProvider')}: {providerData.get(user.id).business_name}
                        </span>
                        {providerData.get(user.id).verified && (
                          <Badge variant="default" className="text-xs">{t('adminPanel.verified')}</Badge>
                        )}
                        <Badge variant="outline" className="text-xs">
                          {providerData.get(user.id).subscription_status}
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/services/provider/${providerData.get(user.id).id}`)}
                        className="text-blue-600 hover:text-blue-700 flex-shrink-0"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        {t('adminPanel.viewInProviders')}
                      </Button>
                    </div>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-2 sm:ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewPosts(user)}
                    className="text-purple-600 hover:text-purple-700 flex-1 min-w-[80px] sm:flex-none"
                  >
                    <Images className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">{t('adminPanel.userPosts.viewPosts')}</span>
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onAddInfraction(user)}
                    className="text-orange-600 hover:text-orange-700 flex-1 min-w-[80px] sm:flex-none"
                  >
                    <AlertTriangle className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">{t('adminPanel.infraction')}</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewHistory(user)}
                    className="flex-1 min-w-[80px] sm:flex-none"
                  >
                    <History className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">{t('adminPanel.history')}</span>
                  </Button>
                  
                  {user.account_status === 'active' || user.account_status === 'warned' ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onSuspendUser(user)}
                        disabled={isSuspending}
                        className="text-orange-600 hover:text-orange-700 flex-1 min-w-[80px] sm:flex-none"
                      >
                        <Clock className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline">{t('adminPanel.suspend')}</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onBanUser(user)}
                        disabled={isBanning}
                        className="text-red-600 hover:text-red-700 flex-1 min-w-[80px] sm:flex-none"
                      >
                        <Ban className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline">{t('adminPanel.ban')}</span>
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onReactivateUser(user)}
                      disabled={isReactivating}
                      className="text-green-600 hover:text-green-700 flex-1 min-w-[100px] sm:flex-none"
                    >
                      <CheckCircle className="h-4 w-4 sm:mr-1" />
                      <span className="hidden sm:inline">{t('adminPanel.reactivate')}</span>
                    </Button>
                  )}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onSendNotification(user)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <Bell className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEditUser(user)}
                    disabled={isUpdating}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDeleteUser(user)}
                    disabled={isDeleting}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default UserManagementTable;
